import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns/format';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import cl from 'classnames';
import {
  Company,
  AggregationDayType,
  AggregationPaymentStatusType,
} from 'src/models';
import { PaymentsChart } from 'src/interface';
import { useContextData, useLanguage } from 'src/hooks';
import { isWeekend } from 'src/helper';
import { useAppDispatch } from 'src/store';
import { clearAnalyticsState } from 'src/store/reducers/analytics';
import {
  usePaymentsAnalytics,
  usePaymentsAnalyticsState,
  usePurchasesAnalytics,
  fetchPaymentsFlowAction,
  fetchPurchasesFlowAction,
} from 'src/services/analytics';
import { useCompanyById } from 'src/services/company';
import { PageSwitcher, OverlapSwitcher } from 'src/components';
import {
  FilterModal,
  FilterModalButton,
  FilterModalIndicator,
  SelectWithLabel,
  useDateRangePicker,
} from 'src/components/general';
import styles from './hotelAnalyticsStyle.module.scss';

type Scale = 1 | 2 | 3 | 4;

type ChartType = 'payments' | 'purchasesByHour';

export const HotelAnalytics = () => {
  const {
    header: { width: headerWidth },
    screen: { isLaptop },
    layoutScroller: { scroll },
  } = useContextData();
  const { hotel: companyId } = useParams() as { hotel: string };
  const company = useCompanyById(companyId) as Company | null;
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const purchasesRecords = usePurchasesAnalytics(companyId);
  const paymentsRecords = usePaymentsAnalytics(companyId);
  const { pending } = usePaymentsAnalyticsState();
  const [aggregation, setAggregation] = useState<AggregationDayType>(
    AggregationDayType.day,
  );
  const [aggregationModal, setAggregationModal] =
    useState<AggregationDayType>(aggregation);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [scale, setScale] = useState<Scale>(1);
  const [chartType, setChartType] = useState<ChartType>('payments');

  useEffect(() => {
    scroll(0, 0);
  }, [scale, scroll]);

  const chartHeight = useMemo(() => (isLaptop ? 225 : 300), [isLaptop]);

  const filterItems = useMemo(
    () =>
      [
        {
          value: AggregationDayType.day,
          label: t('general.day'),
        },
        {
          value: AggregationDayType.week,
          label: t('general.week'),
        },
        {
          value: AggregationDayType.month,
          label: t('general.month'),
        },
      ] as { value: AggregationDayType; label: string }[],
    [t],
  );

  const chartTypes = useMemo(
    () =>
      [
        {
          value: 'payments',
          label: t('page.hotel.analytics.chartType.payments'),
        },
        {
          value: 'purchasesByHour',
          label: t('page.hotel.analytics.chartType.purchasesByHour'),
        },
      ] as { value: ChartType; label: string }[],
    [t],
  );

  const paymentsCount = useMemo(
    () => paymentsRecords.reduce((total, p) => total + p.numberOfPayments, 0),
    [paymentsRecords],
  );

  const paymentsChart: PaymentsChart[] = useMemo(
    () =>
      paymentsRecords.map((p) => ({
        time: format(new Date(p.date), 'dd.MM'),
        date: p.date,
        numberOfPayments: p.numberOfPayments,
        total:
          p.paymentSystemHeadsetSucceeded +
          p.paymentSystemQrCodeSucceeded +
          p.receptionSucceeded +
          p.vipAccessSucceeded,
        system: p.paymentSystemHeadsetSucceeded,
        qrCode: p.paymentSystemQrCodeSucceeded,
        reception: p.receptionSucceeded,
        vipAccess: p.vipAccessSucceeded,
        sessions: p.sessions,
      })),
    [paymentsRecords],
  );

  const purchasesByHour = useMemo(
    () =>
      purchasesRecords.map((p) => ({
        hour: t('page.hotel.analytics.hour', { hour: p.hour }),
        count: p.count,
      })),
    [purchasesRecords, t],
  );

  const purchasesCount = useMemo(
    () => purchasesByHour.reduce((total, p) => total + p.count, 0),
    [purchasesByHour],
  );

  const {
    range,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    clearDRP,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    disabled: pending.payments || pending.purchasesByHour,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  useEffect(() => {
    if (!company) {
      return;
    }

    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    if (chartType === 'payments') {
      dispatch(
        fetchPaymentsFlowAction({
          companyId,
          params: {
            start,
            end,
            aggregation: aggregation,
          },
        }),
      );
    } else {
      dispatch(
        fetchPurchasesFlowAction({
          companyId,
          params: {
            start,
            end,
            statuses: AggregationPaymentStatusType.succeeded,
          },
        }),
      );
    }
  }, [dispatch, chartType, companyId, range, aggregation, company]);

  useEffect(
    () => () => {
      dispatch(clearAnalyticsState());
    },
    [dispatch],
  );

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applyDRPChanges();
          setAggregation(aggregationModal);
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetDRPChanges();
          setAggregationModal(AggregationDayType.day);
        }}
      >
        {modalDateRangePicker}
        {chartType === 'payments' && (
          <SelectWithLabel
            label={t('general.period')}
            value={aggregationModal}
            items={filterItems}
            selectItem={(value) => {
              setAggregationModal(value as AggregationDayType);
            }}
            design="filterModal"
            size="small"
            className={styles.filterModal}
          />
        )}
      </FilterModal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div className={styles.pageSwitcherContainer}>
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <OverlapSwitcher
            currentId={chartType}
            items={chartTypes}
            selectItem={(value) => {
              setChartType(value as ChartType);
            }}
            className={styles.switcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <SelectWithLabel
              label={t('general.scale')}
              value={scale}
              items={
                [
                  { value: 1, label: '100%' },
                  { value: 2, label: '200%' },
                  { value: 3, label: '300%' },
                  { value: 4, label: '400%' },
                ] as { value: Scale; label: string }[]
              }
              selectItem={(value) => {
                setScale(value as Scale);
              }}
              design="filter"
              size="small"
              className={styles.scaleMobileFilter}
            />
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={Boolean(rangeIndicatorText)}
              disabled={pending.payments || pending.purchasesByHour}
            />
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              clear={clearDRP}
              disabled={pending.payments || pending.purchasesByHour}
            />
          </div>
        </div>
        <div className={styles.filters}>
          {dateRangePicker}
          {chartType === 'payments' && (
            <SelectWithLabel
              label={t('general.period')}
              value={aggregation}
              items={filterItems}
              selectItem={(value) => {
                setAggregation(value as AggregationDayType);
                setAggregationModal(value as AggregationDayType);
              }}
              design="filter"
              className={styles.scaleFilter}
              disabled={pending.payments || pending.purchasesByHour}
            />
          )}
          <SelectWithLabel
            label={t('general.scale')}
            value={scale}
            items={
              [
                { value: 1, label: '100%' },
                { value: 2, label: '200%' },
                { value: 3, label: '300%' },
                { value: 4, label: '400%' },
              ] as { value: Scale; label: string }[]
            }
            selectItem={(value) => {
              setScale(value as Scale);
            }}
            design="filter"
            className={styles.scaleFilter}
          />
        </div>
      </div>
      <div
        className={styles.contentSection}
        style={{ width: headerWidth * scale }}
      >
        <div className={styles.chartsSection}>
          <div className={styles.countBlock}>
            <div className={styles.countText} style={{ fontWeight: 600 }}>
              {t('page.hotel.analytics.totalPayments')}
            </div>
            <div className={styles.countText}>
              {chartType === 'payments' ? paymentsCount : purchasesCount}
            </div>
          </div>
          {chartType === 'payments' ? (
            <>
              <div className={styles.chart}>
                <ResponsiveContainer
                  width={'100%'}
                  height={chartHeight}
                  className={cl(pending.payments && styles.opacity)}
                >
                  <BarChart data={paymentsChart}>
                    <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                    <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    {paymentsChart.length > 0 && <Legend />}
                    <Bar
                      dataKey={'numberOfPayments'}
                      fill="#82ca9d"
                      name={t('page.hotel.analytics.numberOfPayments')}
                    >
                      {paymentsChart.map((record) => {
                        const color = isWeekend(record.date)
                          ? '#8884d8'
                          : '#82ca9d';
                        return <Cell key={record.time} fill={color} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {pending.payments && (
                  <div className={styles.loading}>
                    <div className={styles.loader} />
                  </div>
                )}
              </div>
              <div className={styles.chart}>
                <ResponsiveContainer
                  width={'100%'}
                  height={chartHeight}
                  className={cl(pending.payments && styles.opacity)}
                >
                  <BarChart data={paymentsChart}>
                    <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                    <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    {paymentsChart.length > 0 && <Legend />}
                    <Bar
                      dataKey={'total'}
                      fill="#82ca9d"
                      name={t('page.hotel.analytics.total')}
                    >
                      {paymentsChart.map((record) => {
                        const color = isWeekend(record.date)
                          ? '#8884d8'
                          : '#82ca9d';
                        return <Cell key={record.time} fill={color} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {pending.payments && (
                  <div className={styles.loading}>
                    <div className={styles.loader} />
                  </div>
                )}
              </div>
              <div className={styles.chart}>
                <ResponsiveContainer
                  width={'100%'}
                  height={chartHeight}
                  className={cl(pending.payments && styles.opacity)}
                >
                  <BarChart data={paymentsChart}>
                    <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                    <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    {paymentsChart.length > 0 && <Legend />}
                    <Bar
                      dataKey={'sessions'}
                      fill="#82ca9d"
                      name={t('page.hotel.analytics.sessions')}
                    >
                      {paymentsChart.map((record) => {
                        const color = isWeekend(record.date)
                          ? '#8884d8'
                          : '#82ca9d';
                        return <Cell key={record.time} fill={color} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {pending.payments && (
                  <div className={styles.loading}>
                    <div className={styles.loader} />
                  </div>
                )}
              </div>
              <div className={styles.chart}>
                <ResponsiveContainer
                  width={'100%'}
                  height={chartHeight}
                  className={cl(pending.payments && styles.opacity)}
                >
                  <BarChart data={paymentsChart}>
                    <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                    <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    {paymentsChart.length > 0 && <Legend />}
                    <Bar dataKey={'system'} fill="#0088fe" name="SMS/Email" />
                    <Bar
                      dataKey={'qrCode'}
                      fill="#00c49f"
                      name={t('page.hotel.analytics.qrCode')}
                    />
                    <Bar
                      dataKey={'reception'}
                      fill="#8884d8"
                      name={t('page.hotel.analytics.reception')}
                    />
                    <Bar
                      dataKey={'vipAccess'}
                      fill="#ff8042"
                      name={t('page.hotel.analytics.vipAccess')}
                    />
                    <Bar
                      dataKey={'sessions'}
                      fill="#d06299"
                      name={t('page.hotel.analytics.sessions')}
                    />
                  </BarChart>
                </ResponsiveContainer>
                {pending.payments && (
                  <div className={styles.loading}>
                    <div className={styles.loader} />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.chart}>
              <ResponsiveContainer
                width={'100%'}
                height={chartHeight}
                className={cl(pending.purchasesByHour && styles.opacity)}
              >
                <BarChart data={purchasesByHour}>
                  <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey={'count'}
                    fill="#1F93FF"
                    name={t('page.hotel.analytics.count')}
                  />
                  {purchasesRecords.length > 0 && <Legend />}
                </BarChart>
              </ResponsiveContainer>
              {pending.purchasesByHour && (
                <div className={styles.loading}>
                  <div className={styles.loader} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelAnalytics;
